import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useWsEvent } from '@/auth/WsProvider';
import { useChatLightbox } from '@/lib/useChatLightbox';
import { useParentShell } from './ParentShell';

/**
 * Чат родителя со школой — отдельный на каждого ребёнка. Какой ребёнок
 * выбран сверху в шапке (`useParentShell().child`), такой чат и открыт.
 * При переключении ребёнка лента и шапка переезжают.
 *
 * Картинки можно прикреплять кнопкой-скрепкой или drag-and-drop'ом в
 * зону переписки. Сжатие до 1920px — на бэке.
 */
export default function ParentMessagesPage() {
  const { child } = useParentShell();

  const [chatId, setChatId]   = useState(null);
  const [header, setHeader]   = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const scrollerRef = useRef(null);
  const fileRef     = useRef(null);
  const [draft, setDraft]   = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending]     = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Лайтбокс для картинок в этом чате. Привязка делегированная — новые
  // сообщения, пришедшие после mount'а, тоже подхватятся.
  useChatLightbox(chatId ? `chat-${chatId}` : null);

  const reload = useCallback(() => {
    if (!child?.id) return;
    setLoading(true); setError(null);
    setMessages([]); setHeader(null); setChatId(null);
    setDraft(''); setPendingImage(null); setUploadError(null);
    api.get(`/api/chats/by-student?student_id=${child.id}`)
      .then((h) => {
        setHeader(h);
        setChatId(h.id);
        return api.get(`/api/chats/${h.id}/messages`);
      })
      .then((m) => setMessages(m?.items || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, [child?.id]);

  useEffect(reload, [reload]);

  // Помечаем чат прочитанным при открытии (если есть непрочитанные).
  useEffect(() => {
    if (!chatId || !header) return;
    if ((header.unreadCount || 0) === 0) return;
    api.post(`/api/chats/${chatId}/read`)
      .then(() => setHeader((h) => h ? { ...h, unreadCount: 0 } : h))
      .catch(() => {});
  }, [chatId, header?.unreadCount]);

  // Прокручиваем вниз при появлении новых сообщений.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, chatId]);

  // Реалтайм: на любое событие в нашем чате — мерж-обновление + read.
  useWsEvent('chat.message', (msg) => {
    if (!msg?.chatId || msg.chatId !== chatId) return;
    api.get(`/api/chats/${chatId}/messages`)
      .then((m) => setMessages(m?.items || []))
      .catch(() => {});
    if (!msg.read) {
      api.post(`/api/chats/${chatId}/read`).catch(() => {});
    }
  });

  const upload = async (file) => {
    setUploadError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Можно загружать только картинки');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('chat_id', String(chatId));
      form.append('image', file);
      const res = await api.postForm('/api/chats/upload', form);
      setPendingImage({ name: res.imagePath, width: res.width, height: res.height });
    } catch (e) {
      setUploadError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text && !pendingImage) return;
    setSending(true);
    try {
      await api.post(`/api/chats/${chatId}/messages`, {
        text,
        imagePath: pendingImage?.name || '',
      });
      setDraft('');
      setPendingImage(null);
      const m = await api.get(`/api/chats/${chatId}/messages`);
      setMessages(m?.items || []);
    } catch (e) {
      setUploadError(e instanceof ApiError ? e.message : 'Не удалось отправить');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  if (loading) {
    return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем чат…</div>;
  }
  if (error) {
    return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  }
  if (!chatId) return null;

  const klass = header?.studentClass?.code || header?.studentClass?.name || '';
  const subtitle = header?.studentFullName
    ? `${header.studentFullName}${klass ? ' · ' + klass : ''}`
    : 'Администраторы ответят в рабочее время';

  return (
    <div className="pc-body pc-chat">
      <div className="pc-card pc-chat__card">
        <div className="pc-card__head pc-chat__head">
          <div>
            <div className="pc-card__title">Чат со школой</div>
            <div className="pc-chat__sub">{subtitle}</div>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className={'pc-chat__scroller' + (dragOver ? ' is-dragover' : '')}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          {messages.length === 0 && (
            <div className="pc-chat__empty">
              Здесь будут ваши сообщения школе по этому ребёнку.
              Напишите, и администратор ответит.
            </div>
          )}
          {messages.map((m, i) => (
            <ChatBubble key={m.id} chatId={chatId} message={m} prev={messages[i - 1] || null} />
          ))}
        </div>

        <form
          className="pc-chat__compose"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          {pendingImage && (
            <div className="pc-chat__attach">
              <img
                alt=""
                src={`/api/chats/${chatId}/file/${encodeURIComponent(pendingImage.name)}`}
              />
              <button
                type="button"
                className="pc-chat__attach-x"
                onClick={() => setPendingImage(null)}
                title="Убрать"
              >
                ×
              </button>
            </div>
          )}
          {uploadError && (
            <div className="pc-chat__error">{uploadError}</div>
          )}
          <div className="pc-chat__row">
            <button
              type="button"
              className="btn btn--sm pc-chat__attach-btn"
              title="Прикрепить картинку"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || sending}
            >
              <Icon name="paperclip" size={14} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <textarea
              className="pc-chat__input"
              placeholder="Написать сообщение…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              disabled={sending}
            />
            <button
              type="submit"
              className="btn btn--sm pc-chat__send"
              disabled={sending || uploading || (!draft.trim() && !pendingImage)}
            >
              {sending ? '…' : 'Отправить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChatBubble({ chatId, message, prev }) {
  const isOwn = message.senderRole === 'parent';
  const showDateLine = !prev || sameDay(prev.createdAt, message.createdAt) === false;
  return (
    <>
      {showDateLine && (
        <div className="pc-chat__daysep">
          <span>{formatDay(message.createdAt)}</span>
        </div>
      )}
      <div className={'pc-chat__bubble' + (isOwn ? ' is-own' : '')}>
        {message.image && (
          <a
            href={`/api/chats/${chatId}/file/${encodeURIComponent(message.image.name)}`}
            data-fancybox={`chat-${chatId}`}
            data-caption={message.text || ''}
            className="pc-chat__bubble-img-link"
          >
            <img
              src={`/api/chats/${chatId}/file/${encodeURIComponent(message.image.name)}`}
              alt=""
              loading="lazy"
            />
          </a>
        )}
        {message.text && <div className="pc-chat__bubble-text">{message.text}</div>}
        <div className="pc-chat__bubble-time">{shortTime(message.createdAt)}</div>
      </div>
    </>
  );
}

function shortTime(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[4]}:${m[5]}` : '';
}

function formatDay(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const monthsRod = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
  const today = new Date(); today.setHours(0,0,0,0);
  const dt = new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);
  const diff = Math.round((today - dt) / 86400000);
  if (diff === 0) return 'Сегодня';
  if (diff === 1) return 'Вчера';
  return `${Number(m[3])} ${monthsRod[Number(m[2]) - 1]} ${m[1]}`;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return a.slice(0, 10) === b.slice(0, 10);
}
